import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  Table, Button, Space, Card, Modal, Form, Input, Select, Tag, Popconfirm, message, 
  Tooltip, Tabs, Descriptions, Row, Col, Badge, Checkbox, Divider, InputNumber 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined,
  EnvironmentOutlined, CalendarOutlined, EyeOutlined, CheckCircleOutlined, StopOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import {
  fetchClusters, createCluster, updateCluster, deleteCluster, copyClustersToAcademicYear,
  fetchClusterInstitutions, createClusterInstitution, updateClusterInstitution, deleteClusterInstitution,
  saveClusterInstitutionStandardStrength,
  fetchClusterAccess, grantClusterAccess, revokeClusterAccess, fetchStudioLogins,
  fetchClusterStandards, createClusterStandard, updateClusterStandard, deleteClusterStandard
} from '../../../../Service/Clusters_API/clustersApi';
import { fetchAcademicYears } from '../../../../Service/Dashboard_API/dashboardApi';
import { fetchStaff } from '../../../../Service/Staff_API/staffApi';
import { fetchInstitutions } from '../../../../Service/Institutions_API/institutionsApi';
import { fetchStandards } from '../../../../Service/Syllabus_API/syllabusApi';
import ClusterAttendanceTab from './ClusterAttendanceTab';
import ClusterLiveAttendanceReportCard from '../../DashboardPages/DashboardComponents/ClusterLiveAttendanceReportCard';

const formatDate = (dateInput) => {
  if (!dateInput) return 'N/A';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return dateInput;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const ClusterManagementPage = ({ userRole: propUserRole }) => {
  const authRole = useSelector((state) => state.auth?.role);
  const userRole = propUserRole || authRole || 'USER';

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [staff, setStaff] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  
  // Active selection states
  const [selectedYearId, setSelectedYearId] = useState(undefined);
  const [searchText, setSearchText] = useState('');
  const [selectedMedium, setSelectedMedium] = useState(undefined);
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Details Modal state
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [clusterInstitutions, setClusterInstitutions] = useState([]);
  const [instLoading, setInstLoading] = useState(false);

  // Link Institution Modal state
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  // Institution Candidate Strength Modal state
  const [isStrengthModalOpen, setIsStrengthModalOpen] = useState(false);
  const [selectedStrengthInst, setSelectedStrengthInst] = useState(null);
  const [savingStrength, setSavingStrength] = useState(false);

  // Institution View Modal state
  const [selectedInstRecord, setSelectedInstRecord] = useState(null);
  const [isInstDetailsOpen, setIsInstDetailsOpen] = useState(false);

  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [strengthForm] = Form.useForm();
  const [accessForm] = Form.useForm();

  // Cluster Access states
  const [clusterAccessList, setClusterAccessList] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [availableLogins, setAvailableLogins] = useState([]);
  const [grantLoading, setGrantLoading] = useState(false);

  // Cluster Standards states
  const [clusterStandards, setClusterStandards] = useState([]);
  const [stdLoading, setStdLoading] = useState(false);
  const [allStandardsList, setAllStandardsList] = useState([]);
  const [isLinkStandardOpen, setIsLinkStandardOpen] = useState(false);
  const [editingClusterStandard, setEditingClusterStandard] = useState(null);
  const [standardForm] = Form.useForm();

  // Copy Cluster Modal state
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyModalCluster, setCopyModalCluster] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyForm] = Form.useForm();

  // Active Academic Year calculation
  const activeAcademicYear = academicYears.find(
    y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE'
  ) || academicYears.find(y => !y.status) || academicYears[0];
  const activeYearId = activeAcademicYear?.academic_year_id;
  const clusterYearId = selectedCluster?.academic_year_id || selectedYearId || activeYearId;
  const clusterAcademicYear = academicYears.find(ay => String(ay.academic_year_id) === String(clusterYearId)) || activeAcademicYear;

  useEffect(() => {
    loadAcademicYearsList();
    loadInstitutionsList();
    loadStudioLoginsList();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (searchText && searchText.trim()) filters.search = searchText.trim();
      if (selectedMedium && selectedMedium !== 'all') filters.medium = selectedMedium;
      if (selectedStatus && selectedStatus !== 'all') filters.status = selectedStatus;

      const result = await fetchClusters(selectedYearId, filters);
      setData(result);
    } catch (e) {
      message.error('Failed to load clusters.');
    } finally {
      setLoading(false);
    }
  }, [selectedYearId, searchText, selectedMedium, selectedStatus]);

  useEffect(() => {
    if (selectedYearId !== undefined) {
      loadData();
    }
  }, [selectedYearId, loadData]);

  const loadAcademicYearsList = async () => {
    try {
      const years = await fetchAcademicYears();
      setAcademicYears(years);
      // Select the active year by default
      const active = years.find(y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE');
      if (active) {
        setSelectedYearId(active.academic_year_id);
      } else if (years.length > 0) {
        setSelectedYearId(years[0].academic_year_id);
      }
    } catch (e) {
      console.warn('Failed to load academic years');
    }
  };

  const loadStaffList = useCallback(async (yearId) => {
    try {
      const params = {};
      if (yearId && yearId !== 'all') params.academic_year_id = yearId;
      const list = await fetchStaff(params);
      setStaff(list || []);
    } catch (e) {
      console.warn('Failed to load staff list');
    }
  }, []);

  useEffect(() => {
    loadStaffList(selectedYearId);
  }, [selectedYearId, loadStaffList]);

  const loadInstitutionsList = async () => {
    try {
      const list = await fetchInstitutions();
      setInstitutions(list);
    } catch (e) {
      console.warn('Failed to load institutions list');
    }
  };

  const loadStudioLoginsList = async () => {
    try {
      const logins = await fetchStudioLogins();
      setAvailableLogins(logins);
    } catch (e) {
      console.warn('Failed to load studio logins:', e.message);
    }
  };

  // Fetch institutions linked to the selected cluster
  const loadClusterInstitutions = async (clusterId, yearId) => {
    setInstLoading(true);
    try {
      const targetYearId = yearId || selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      const result = await fetchClusterInstitutions(clusterId, targetYearId);
      setClusterInstitutions(result);
    } catch (e) {
      message.error('Failed to load linked institutions.');
    } finally {
      setInstLoading(false);
    }
  };

  // Fetch standards linked to the selected cluster
  const loadClusterStandards = async (clusterId, yearId) => {
    setStdLoading(true);
    try {
      const targetYearId = yearId || selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      const result = await fetchClusterStandards(clusterId, targetYearId);
      const filtered = (result || []).filter(cs => 
        !targetYearId || cs.academic_year_id === targetYearId || cs.academic_year_id === Number(targetYearId) || !cs.academic_year_id
      );
      setClusterStandards(filtered);
    } catch (e) {
      message.error('Failed to load linked standards.');
    } finally {
      setStdLoading(false);
    }
  };

  // Fetch all master standards
  const loadAllStandardsList = async (yearId) => {
    try {
      const targetYearId = yearId || selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      const list = await fetchStandards(targetYearId);
      const activeYearFiltered = (list || []).filter(std => 
        !targetYearId || !std.academic_year_id || std.academic_year_id === targetYearId || std.academic_year_id === Number(targetYearId)
      );
      setAllStandardsList(activeYearFiltered);
    } catch (e) {
      console.warn('Failed to load standards list');
    }
  };

  const handleSaveClusterStandard = async (values) => {
    try {
      const targetYearId = selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      if (editingClusterStandard) {
        await updateClusterStandard(editingClusterStandard.cluster_standard_id, {
          instructor_medium: values.instructor_medium,
          status: values.status
        });
        message.success('Cluster standard updated successfully.');
      } else {
        await createClusterStandard({
          cluster_id: selectedCluster.cluster_id,
          academic_year_id: targetYearId,
          standard_id: values.standard_id,
          instructor_medium: values.instructor_medium,
          status: values.status || 'Active'
        });
        message.success('Standard linked to cluster successfully.');
      }
      setIsLinkStandardOpen(false);
      standardForm.resetFields();
      setEditingClusterStandard(null);
      loadClusterStandards(selectedCluster.cluster_id, targetYearId);
    } catch (e) {
      message.error(e.message || 'Failed to save cluster standard mapping.');
    }
  };

  const handleToggleClusterStandardStatus = async (record) => {
    try {
      const targetYearId = selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      const newStatus = record.status === 'Active' ? 'Inactive' : 'Active';
      await updateClusterStandard(record.cluster_standard_id, {
        instructor_medium: record.instructor_medium,
        status: newStatus
      });
      message.success(`Standard status updated to ${newStatus}.`);
      loadClusterStandards(selectedCluster.cluster_id, targetYearId);
    } catch (e) {
      message.error('Failed to update standard status.');
    }
  };

  const handleUnlinkClusterStandard = async (id) => {
    try {
      const targetYearId = selectedCluster?.academic_year_id || selectedYearId || activeYearId;
      await deleteClusterStandard(id);
      message.success('Standard unlinked from cluster successfully.');
      loadClusterStandards(selectedCluster.cluster_id, targetYearId);
    } catch (e) {
      message.error('Failed to unlink standard.');
    }
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        ...values,
        academic_year_id: selectedYearId
      };
      if (editingItem) {
        await updateCluster(editingItem.cluster_id, payload);
        message.success('Cluster updated successfully.');
      } else {
        await createCluster(payload);
        message.success('Cluster created successfully.');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingItem(null);
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to save cluster.');
    }
  };

  const handleOpenCopyModal = (record = null) => {
    setCopyModalCluster(record);
    const srcAyId = record ? record.academic_year_id : selectedYearId;
    const otherAys = academicYears.filter(ay => String(ay.academic_year_id) !== String(srcAyId));
    const defaultTargetAy = otherAys.length > 0 ? otherAys[0].academic_year_id : null;

    let selectedClusterIds = [];
    if (record) {
      selectedClusterIds = [record.cluster_id];
    } else if (selectedYearId) {
      selectedClusterIds = data
        .filter(c => String(c.academic_year_id) === String(selectedYearId))
        .map(c => c.cluster_id);
    }

    copyForm.setFieldsValue({
      source_cluster_ids: selectedClusterIds,
      target_academic_year_id: defaultTargetAy,
      copy_institutions: true
    });
    setIsCopyModalOpen(true);
  };

  const handleCopySubmit = async (values) => {
    setCopyLoading(true);
    try {
      const payload = {
        source_cluster_ids: values.source_cluster_ids,
        target_academic_year_id: values.target_academic_year_id,
        copy_institutions: values.copy_institutions !== false,
        copy_standards: false
      };

      const res = await copyClustersToAcademicYear(payload);
      message.success(res.message || 'Cluster and linked institutions copied successfully!');
      setIsCopyModalOpen(false);
      copyForm.resetFields();
      loadData();
    } catch (error) {
      message.error(error.message || 'Failed to copy cluster and linked institutions.');
    } finally {
      setCopyLoading(false);
    }
  };

  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.status === 'Active' ? 'Inactive' : 'Active';
      await updateCluster(record.cluster_id, {
        ...record,
        status: newStatus
      });
      message.success(`Cluster status updated to ${newStatus}.`);
      loadData();
    } catch (e) {
      message.error('Failed to update cluster status.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCluster(id);
      message.success('Cluster deleted successfully.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to delete cluster.');
    }
  };

  // --- Link Institution Actions ---
  const handleOpenEditLink = (record) => {
    if (!record) return;
    setEditingLink(record);
    const initialStrengths = {};
    (record.standard_strengths || []).forEach(st => {
      if (st && st.standard_id) {
        initialStrengths[st.standard_id] = st.candidate_strength || 0;
      }
    });
    (clusterStandards || []).forEach(st => {
      if (st && st.standard_id && initialStrengths[st.standard_id] === undefined) {
        initialStrengths[st.standard_id] = 0;
      }
    });
    linkForm.setFieldsValue({
      cluster_institution_code: record.cluster_institution_code,
      cluster_institution_equipment_type: record.cluster_institution_equipment_type,
      status: record.status || 'Active',
      standard_strengths: initialStrengths
    });
    setIsLinkOpen(true);
  };

  const handleOpenNewLink = () => {
    setEditingLink(null);
    linkForm.resetFields();
    const initialStrengths = {};
    (clusterStandards || []).forEach(st => {
      if (st && st.standard_id) {
        initialStrengths[st.standard_id] = 0;
      }
    });
    linkForm.setFieldsValue({
      cluster_institution_equipment_type: 'MANIPAL FOUNDATION',
      standard_strengths: initialStrengths
    });
    setIsLinkOpen(true);
  };

  const handleOpenStrengthModal = (record) => {
    if (!record) return;
    setSelectedStrengthInst(record);
    const initialStrengths = {};
    (record.standard_strengths || []).forEach(st => {
      if (st && st.standard_id) {
        initialStrengths[st.standard_id] = st.candidate_strength || 0;
      }
    });
    (clusterStandards || []).forEach(st => {
      if (st && st.standard_id && initialStrengths[st.standard_id] === undefined) {
        initialStrengths[st.standard_id] = 0;
      }
    });
    strengthForm.setFieldsValue({
      standard_strengths: initialStrengths
    });
    setIsStrengthModalOpen(true);
  };

  const parseStandardStrengths = (rawStrengths) => {
    if (!rawStrengths) return [];
    const result = [];
    if (Array.isArray(rawStrengths)) {
      rawStrengths.forEach((item, index) => {
        if (item === undefined || item === null) return;
        if (typeof item === 'object') {
          const sid = parseInt(item.standard_id || item.id);
          const strength = parseInt(item.candidate_strength !== undefined ? item.candidate_strength : (item.strength || 0));
          if (!isNaN(sid)) {
            result.push({ standard_id: sid, candidate_strength: isNaN(strength) ? 0 : strength });
          }
        } else {
          const sid = parseInt(index);
          const strength = parseInt(item);
          if (!isNaN(sid) && sid > 0) {
            result.push({ standard_id: sid, candidate_strength: isNaN(strength) ? 0 : strength });
          }
        }
      });
    } else if (typeof rawStrengths === 'object') {
      Object.entries(rawStrengths).forEach(([k, v]) => {
        const sid = parseInt(k);
        if (!isNaN(sid)) {
          let val = 0;
          if (typeof v === 'object' && v !== null) {
            val = parseInt(v.candidate_strength !== undefined ? v.candidate_strength : (v.strength || 0)) || 0;
          } else {
            val = parseInt(v) || 0;
          }
          result.push({ standard_id: sid, candidate_strength: isNaN(val) ? 0 : val });
        }
      });
    }
    return result;
  };

  const handleSaveStrengthsDirect = async (values) => {
    if (!selectedStrengthInst) return;
    setSavingStrength(true);
    try {
      const stdStrengths = parseStandardStrengths(values?.standard_strengths);

      await saveClusterInstitutionStandardStrength(selectedStrengthInst.cluster_institution_id, {
        standard_strengths: stdStrengths,
        cluster_id: selectedCluster.cluster_id,
        institution_id: selectedStrengthInst.institution_id,
        academic_year_id: selectedCluster?.academic_year_id || selectedYearId
      });
      message.success('Candidate strength updated successfully.');
      setIsStrengthModalOpen(false);
      loadClusterInstitutions(selectedCluster.cluster_id);
    } catch (err) {
      console.error('Failed to save candidate strength:', err);
      message.error(err.message || 'Failed to save candidate strength.');
    } finally {
      setSavingStrength(false);
    }
  };

  const handleSaveLink = async (values) => {
    try {
      const stdStrengths = parseStandardStrengths(values?.standard_strengths);

      if (editingLink) {
        await updateClusterInstitution(editingLink.cluster_institution_id, {
          cluster_institution_code: values.cluster_institution_code,
          cluster_institution_equipment_type: values.cluster_institution_equipment_type,
          status: values.status,
          cluster_id: selectedCluster.cluster_id,
          institution_id: editingLink.institution_id,
          academic_year_id: selectedCluster?.academic_year_id || selectedYearId,
          standard_strengths: stdStrengths
        });
        message.success('Institution link and candidate strengths updated successfully.');
      } else {
        await createClusterInstitution({
          cluster_id: selectedCluster.cluster_id,
          institution_id: values.institution_id,
          academic_year_id: selectedCluster?.academic_year_id || selectedYearId,
          cluster_institution_code: values.cluster_institution_code,
          cluster_institution_equipment_type: values.cluster_institution_equipment_type,
          standard_strengths: stdStrengths
        });
        message.success('Institution linked and candidate strengths saved successfully.');
      }
      setIsLinkOpen(false);
      linkForm.resetFields();
      setEditingLink(null);
      loadClusterInstitutions(selectedCluster.cluster_id);
    } catch (e) {
      console.error('Failed to save institution link:', e);
      message.error(e.message || 'Failed to save institution link.');
    }
  };

  const handleUnlink = async (ciId) => {
    try {
      await deleteClusterInstitution(ciId);
      message.success('Institution unlinked successfully.');
      loadClusterInstitutions(selectedCluster.cluster_id);
    } catch (e) {
      message.error('Failed to unlink institution.');
    }
  };

  const loadClusterAccessList = async (clusterId) => {
    setAccessLoading(true);
    try {
      // Run both fetches independently so studio logins always load
      const [accessResult, loginsResult] = await Promise.allSettled([
        fetchClusterAccess(clusterId),
        fetchStudioLogins()
      ]);

      if (accessResult.status === 'fulfilled') {
        setClusterAccessList(accessResult.value);
      } else {
        console.error('Failed to load cluster access:', accessResult.reason);
        setClusterAccessList([]);
      }

      if (loginsResult.status === 'fulfilled') {
        setAvailableLogins(loginsResult.value);
      } else {
        console.error('Failed to load studio logins:', loginsResult.reason);
      }
    } catch (e) {
      message.error('Failed to load cluster access list.');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleGrantAccess = async (values) => {
    setGrantLoading(true);
    try {
      await grantClusterAccess(selectedCluster.cluster_id, {
        login_ids: values.login_ids,
        academic_year_id: selectedYearId
      });
      message.success('Access granted successfully.');
      accessForm.resetFields(['login_ids']);
      const list = await fetchClusterAccess(selectedCluster.cluster_id);
      setClusterAccessList(list);
    } catch (e) {
      message.error(e.message || 'Failed to grant access.');
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    try {
      await revokeClusterAccess(accessId);
      message.success('Access revoked successfully.');
      const list = await fetchClusterAccess(selectedCluster.cluster_id);
      setClusterAccessList(list);
    } catch (e) {
      message.error('Failed to revoke access.');
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedMedium(undefined);
    setSelectedStatus(undefined);
  };

  // Filter clusters on frontend
  const filteredData = data.filter(item => {
    const matchesYear = item.academic_year_id === selectedYearId;
    
    const nameMatch = item.cluster_name ? item.cluster_name.toLowerCase().includes(searchText.toLowerCase()) : false;
    const codeMatch = item.cluster_code ? item.cluster_code.toLowerCase().includes(searchText.toLowerCase()) : false;
    const locationMatch = item.location ? item.location.toLowerCase().includes(searchText.toLowerCase()) : false;
    const matchesSearch = !searchText || nameMatch || codeMatch || locationMatch;
    
    const matchesMedium = !selectedMedium || item.cluster_medium === selectedMedium;
    const matchesStatus = !selectedStatus || item.status === selectedStatus;
    
    return matchesYear && matchesSearch && matchesMedium && matchesStatus;
  });

  // Target Academic Year for filtering staff options
  const targetStaffYearId = selectedYearId || activeYearId;

  // Staff lists filtered by type and academic year
  const technicalSupportStaff = staff.filter(s => 
    (s.staff_type === 'IT-SUPPORT' || s.staff_type === 'TECHNICAL SUPPORT') &&
    (!s.academic_year_id || !targetStaffYearId || String(s.academic_year_id) === String(targetStaffYearId))
  );

  const coordinatorStaff = staff.filter(s => 
    (s.staff_type === 'COORDINATOR' || s.staff_type === 'COORDINATOR STAFF') &&
    (!s.academic_year_id || !targetStaffYearId || String(s.academic_year_id) === String(targetStaffYearId))
  );

  const columns = [
    { title: 'Cluster Name', dataIndex: 'cluster_name', key: 'cluster_name', render: (text) => <strong>{text}</strong> },
    { title: 'Code', dataIndex: 'cluster_code', key: 'cluster_code' },
    {
      title: 'Medium',
      dataIndex: 'cluster_medium',
      key: 'cluster_medium',
      render: (m) => <Tag color={m === 'english' ? 'blue' : m === 'kannada' ? 'warning' : 'default'}>{(m || 'N/A').toUpperCase()}</Tag>
    },
    { title: 'Est. Year', dataIndex: 'cluster_statrt_year', key: 'cluster_statrt_year', render: (y) => y || 'N/A' },
    { title: 'Location', dataIndex: 'location', key: 'location', render: (loc) => loc || 'N/A' },
    { title: 'Technical Support', dataIndex: 'tech_support_staff_name', key: 'tech_support_staff_name', render: (name) => name || <span style={{ color: 'rgba(255,255,255,0.25)' }}>Not Assigned</span> },
    { title: 'Coordinator', dataIndex: 'coordinator_staff_name', key: 'coordinator_staff_name', render: (name) => name || <span style={{ color: 'rgba(255,255,255,0.25)' }}>Not Assigned</span> },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'Active' ? 'green' : 'red'}>{(status || 'Active').toUpperCase()}</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => {
              const targetYearId = record.academic_year_id || selectedYearId || activeYearId;
              setSelectedCluster(record);
              loadClusterInstitutions(record.cluster_id, targetYearId);
              loadClusterStandards(record.cluster_id, targetYearId);
              loadAllStandardsList(targetYearId);
              if (userRole === 'ADMIN') {
                loadClusterAccessList(record.cluster_id);
              }
              setIsDetailsOpen(true);
            }} />
          </Tooltip>
          {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
            <Tooltip title="Copy Cluster & Linked Institutions to Academic Year">
              <Button 
                size="small" 
                icon={<CopyOutlined style={{ color: '#1890ff' }} />} 
                onClick={() => handleOpenCopyModal(record)} 
              />
            </Tooltip>
          )}
          {userRole === 'ADMIN' && (
            <Tooltip title="Edit Cluster">
              <Button size="small" icon={<EditOutlined />} onClick={() => {
                setEditingItem(record);
                form.setFieldsValue({
                  ...record,
                  tech_support_staff_id: record.tech_support_staff_id || undefined,
                  coordinator_staff_id: record.coordinator_staff_id || undefined
                });
                setIsModalOpen(true);
              }} />
            </Tooltip>
          )}
          {userRole === 'ADMIN' && (
            <Tooltip title={record.status === 'Active' ? 'Deactivate' : 'Activate'}>
              <Button 
                size="small" 
                style={{ color: record.status === 'Active' ? '#f43f5e' : '#10b981', borderColor: record.status === 'Active' ? '#f43f5e' : '#10b981' }} 
                icon={record.status === 'Active' ? <StopOutlined /> : <CheckCircleOutlined />} 
                onClick={() => handleToggleStatus(record)} 
              />
            </Tooltip>
          )}
          {userRole === 'ADMIN' && (
            <Popconfirm title="Delete this cluster?" onConfirm={() => handleDelete(record.cluster_id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <Tabs
        defaultActiveKey="attendance-summary"
        items={[
          {
            key: 'attendance-summary',
            label: 'Cluster Attendance Summary',
            children: (
              <div style={{ padding: '8px 0' }}>
                <ClusterLiveAttendanceReportCard
                  academicYears={academicYears}
                  clusters={data}
                  userRoleOverride={userRole}
                />
              </div>
            )
          },
          {
            key: 'attendance',
            label: 'Cluster Attendance',
            children: (
              <ClusterAttendanceTab 
                userRole={userRole} 
                clusters={filteredData} 
                loadingClusters={loading} 
                academicYearId={selectedYearId}
              />
            )
          },
          {
            key: 'management',
            label: 'Cluster Management',
            children: (
              <>
                {/* Header Panel */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Cluster Management</h2>
        </div>
        <Space>
          {academicYears.length > 0 && (
            <Select showSearch optionFilterProp="children" 
              value={selectedYearId}
              onChange={(val) => { setSelectedYearId(val); }}
              style={{ minWidth: 200 }}
            >
              {academicYears.map(year => (
                <Select.Option key={year.academic_year_id} value={year.academic_year_id}>
                  {year.academic_year} {year.status === 'active' ? '(Active)' : ''}
                </Select.Option>
              ))}
            </Select>
          )}
          {userRole === 'ADMIN' && (
            <Button 
              icon={<CopyOutlined style={{ color: '#1890ff' }} />} 
              onClick={() => handleOpenCopyModal(null)}
            >
              Copy Cluster to Academic Year
            </Button>
          )}
          {userRole === 'ADMIN' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setIsModalOpen(true); }}>
              Add Cluster
            </Button>
          )}
        </Space>
      </div>

      {/* Filter Options Panel */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <Input 
          placeholder="Search by name, code or location..." 
          onChange={(e) => setSearchText(e.target.value)} 
          value={searchText} 
          style={{ width: 300, borderRadius: 6 }} 
          allowClear 
        />
        <Select showSearch optionFilterProp="children" 
          placeholder="All Mediums" 
          onChange={(value) => setSelectedMedium(value)} 
          value={selectedMedium} 
          style={{ width: 140 }} 
          allowClear
        >
          <Select.Option value="english">English</Select.Option>
          <Select.Option value="kannada">Kannada</Select.Option>
        </Select>
        <Select showSearch optionFilterProp="children" 
          placeholder="All Statuses" 
          onChange={(value) => setSelectedStatus(value)} 
          value={selectedStatus} 
          style={{ width: 140 }} 
          allowClear
        >
          <Select.Option value="Active">Active</Select.Option>
          <Select.Option value="Inactive">Inactive</Select.Option>
        </Select>
        {(searchText || selectedMedium || selectedStatus) && (
          <Button type="link" onClick={handleClearFilters} style={{ padding: 0 }}>
            Clear Filters
          </Button>
        )}
      </div>

      <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
        <Table dataSource={filteredData} columns={columns} rowKey="cluster_id" loading={loading} scroll={{ x: 'max-content' }} pagination={{ pageSize: 20 }} />
      </Card>

      {/* Add / Edit Cluster Modal */}
      <Modal title={editingItem ? 'Edit Cluster' : 'Add Cluster'} open={isModalOpen} onCancel={() => setIsModalOpen(false)} footer={null} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="cluster_name" label="Cluster Name" rules={[{ required: true, message: 'Please input cluster name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="cluster_code" label="Cluster Code" rules={[{ required: true, message: 'Please input cluster code!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="cluster_medium" label="Medium" rules={[{ required: true }]} initialValue="english">
            <Select showSearch optionFilterProp="children">
              <Select.Option value="english">English</Select.Option>
              <Select.Option value="kannada">Kannada</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input prefix={<EnvironmentOutlined />} placeholder="Enter location name" />
          </Form.Item>
          <Form.Item name="cluster_statrt_year" label="Start Year">
            <Input prefix={<CalendarOutlined />} placeholder="e.g. 2026" />
          </Form.Item>
          <Form.Item name="tech_support_staff_id" label="Technical Support Staff">
            <Select showSearch optionFilterProp="children" placeholder="Assign Technical Support Staff (Optional)" allowClear>
              {technicalSupportStaff.map(s => (
                <Select.Option key={s.staff_id} value={s.staff_id}>{s.staff_name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="coordinator_staff_id" label="Coordinator Staff">
            <Select showSearch optionFilterProp="children" placeholder="Assign Coordinator Staff (Optional)" allowClear>
              {coordinatorStaff.map(s => (
                <Select.Option key={s.staff_id} value={s.staff_id}>{s.staff_name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          {editingItem && (
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="children">
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Cluster Details Drawer/Modal */}
      <Modal 
        title={selectedCluster ? `${selectedCluster.cluster_name}${selectedCluster.academic_year_id && academicYears.find(ay => ay.academic_year_id === selectedCluster.academic_year_id) ? ` - ${academicYears.find(ay => ay.academic_year_id === selectedCluster.academic_year_id).academic_year}` : ''} - Information Panel` : ''} 
        open={isDetailsOpen} 
        onCancel={() => setIsDetailsOpen(false)} 
        footer={null} 
        width={1150}
        destroyOnClose
      >
        {selectedCluster && (
          <Tabs defaultActiveKey="details" items={[
            {
              key: 'details',
              label: 'Cluster Details',
              children: (
                <div style={{ padding: '12px 0' }}>
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Cluster Code">{selectedCluster.cluster_code}</Descriptions.Item>
                    <Descriptions.Item label="Cluster Name">{selectedCluster.cluster_name}</Descriptions.Item>
                    <Descriptions.Item label="Medium">
                      <Tag color={selectedCluster.cluster_medium === 'english' ? 'blue' : 'warning'}>
                        {(selectedCluster.cluster_medium || 'N/A').toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Start Year">{selectedCluster.cluster_statrt_year || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Location" span={2}>
                      <Space>
                        <EnvironmentOutlined style={{ color: '#f43f5e' }} />
                        {selectedCluster.location || 'N/A'}
                        {selectedCluster.location && (
                          <a 
                            href={`https://maps.google.com/?q=${encodeURIComponent(selectedCluster.location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12 }}
                          >
                            Open Google Maps
                          </a>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={selectedCluster.status === 'Active' ? 'green' : 'red'}>
                        {(selectedCluster.status || 'Active').toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Year">{selectedCluster.academic_year || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Created">{selectedCluster.created_at ? formatDate(selectedCluster.created_at) : 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated">{selectedCluster.updated_at ? formatDate(selectedCluster.updated_at) : 'N/A'}</Descriptions.Item>
                  </Descriptions>
                </div>
              )
            },
            {
              key: 'institutions',
              label: `Linked Institutions (${clusterInstitutions.length})`,
              children: (
                <div style={{ padding: '12px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0 }}>Institution Affiliations</h4>
                    {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleOpenNewLink}>
                        Link Institution
                      </Button>
                    )}
                  </div>
                  <Table 
                    dataSource={clusterInstitutions} 
                    loading={instLoading} 
                    rowKey="cluster_institution_id"
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 20 }}
                    columns={[
                      { title: 'Institution Code', dataIndex: 'cluster_institution_code', key: 'cluster_institution_code', render: (code) => <Badge status="processing" text={code || 'N/A'} />, sorter: (a, b) => (a.cluster_institution_code || '').localeCompare(b.cluster_institution_code || ''), defaultSortOrder: 'ascend' },
                      { title: 'Institution Name', dataIndex: 'institution_name', key: 'institution_name', render: (text, rec) => (
                          <div>
                            <strong>{text}</strong>
                            {rec.institution_head && <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>Principal: {rec.institution_head}</div>}
                          </div>
                        )
                      },
                      {
                        title: 'Candidate Strength',
                        key: 'candidate_strength',
                        render: (_, rec) => {
                          const stds = (rec?.standard_strengths || []).filter(s => s && s.standard_id);
                          if (stds.length === 0) {
                            return <Tag color="default">0 Candidates</Tag>;
                          }
                          return (
                            <Space wrap size={4}>
                              {stds.map(s => (
                                <Tag key={s.standard_id} color="cyan" style={{ fontSize: 11 }}>
                                  {s.standard_name || `Std #${s.standard_id}`}: <strong>{s.candidate_strength || 0}</strong>
                                </Tag>
                              ))}
                            </Space>
                          );
                        }
                      },
                      { title: 'Equipment Type', dataIndex: 'cluster_institution_equipment_type', key: 'cluster_institution_equipment_type', render: (eq) => <Tag color="purple">{eq || 'N/A'}</Tag> },
                      { title: 'Status', dataIndex: 'status', key: 'status', render: (st) => <Tag color={st === 'Active' ? 'green' : 'red'}>{st}</Tag> },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, record) => (
                          <Space>
                            <Tooltip title="View Institution Details">
                              <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                  setSelectedInstRecord(record);
                                  setIsInstDetailsOpen(true);
                                }}
                              />
                            </Tooltip>
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                              <Tooltip title="Manage Candidate Strength">
                                <Button
                                  size="small"
                                  style={{ color: '#0891b2', borderColor: '#0891b2' }}
                                  icon={<UsergroupAddOutlined />}
                                  onClick={() => handleOpenStrengthModal(record)}
                                />
                              </Tooltip>
                            )}
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                              <Tooltip title="Edit Affiliation">
                                <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEditLink(record)} />
                              </Tooltip>
                            )}
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                              <Popconfirm title="Unlink this Institution?" onConfirm={() => handleUnlink(record.cluster_institution_id)}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            )}
                          </Space>
                        )
                      }
                    ]} 
                  />
                </div>
              )
            },
            {
              key: 'standards',
              label: `Linked Standards (${clusterStandards.length})`,
              children: (
                <div style={{ padding: '12px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <h4 style={{ margin: 0 }}>Cluster Standards Mapping</h4>
                      {clusterAcademicYear && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          Academic Year: <Tag color="blue" style={{ marginLeft: 4 }}>{clusterAcademicYear.academic_year}</Tag>
                        </div>
                      )}
                    </div>
                    {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                      <Button 
                        type="primary" 
                        size="small" 
                        icon={<PlusOutlined />} 
                        onClick={() => { 
                          setEditingClusterStandard(null); 
                          standardForm.resetFields(); 
                          loadAllStandardsList(selectedCluster?.academic_year_id || selectedYearId || activeYearId);
                          setIsLinkStandardOpen(true); 
                        }}
                      >
                        Link Standard
                      </Button>
                    )}
                  </div>
                  <Table 
                    dataSource={clusterStandards} 
                    loading={stdLoading} 
                    rowKey="cluster_standard_id"
                    size="small"
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: 'Standard Code', dataIndex: 'standard_code', key: 'standard_code', render: (code) => <Badge status="processing" text={code || 'N/A'} /> },
                      { title: 'Standard Name', dataIndex: 'standard_name', key: 'standard_name', render: (text) => <strong>{text}</strong> },
                      { 
                        title: 'Instructor Medium', 
                        dataIndex: 'instructor_medium', 
                        key: 'instructor_medium', 
                        render: (m) => (
                          <Tag color={m === 'ENGLISH' ? 'blue' : m === 'KANNADA' ? 'warning' : 'purple'}>
                            {m || 'N/A'}
                          </Tag>
                        ) 
                      },
                      { 
                        title: 'Status', 
                        dataIndex: 'status', 
                        key: 'status', 
                        render: (st) => <Tag color={st === 'Active' ? 'green' : 'red'}>{st || 'Active'}</Tag> 
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, record) => (
                          <Space>
                            {userRole === 'ADMIN' && (
                              <Tooltip title="Edit Instructor Medium & Status">
                                <Button size="small" icon={<EditOutlined />} onClick={() => {
                                  setEditingClusterStandard(record);
                                  standardForm.setFieldsValue({
                                    standard_id: record.standard_id,
                                    instructor_medium: record.instructor_medium,
                                    status: record.status || 'Active'
                                  });
                                  setIsLinkStandardOpen(true);
                                }} />
                              </Tooltip>
                            )}
                            {userRole === 'ADMIN' && (
                              <Tooltip title={record.status === 'Active' ? 'Deactivate Standard' : 'Activate Standard'}>
                                <Button 
                                  size="small" 
                                  style={{ color: record.status === 'Active' ? '#f43f5e' : '#10b981', borderColor: record.status === 'Active' ? '#f43f5e' : '#10b981' }} 
                                  icon={record.status === 'Active' ? <StopOutlined /> : <CheckCircleOutlined />} 
                                  onClick={() => handleToggleClusterStandardStatus(record)} 
                                />
                              </Tooltip>
                            )}
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                              <Popconfirm title="Unlink this standard from cluster?" onConfirm={() => handleUnlinkClusterStandard(record.cluster_standard_id)}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            )}
                          </Space>
                        )
                      }
                    ]} 
                  />
                </div>
              )
            },
            {
              key: 'staff',
              label: 'Assigned Staff',
              children: (
                <div style={{ padding: '12px 0' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card title="Technical Support" size="small" style={{ background: 'transparent', border: '1px solid var(--border-color)' }}>
                        {selectedCluster.tech_support_staff_name ? (
                          <div style={{ color: 'var(--text-color)' }}>
                            <h3 style={{ marginBottom: 8, color: 'var(--text-color)' }}>{selectedCluster.tech_support_staff_name}</h3>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Code:</strong> {selectedCluster.tech_support_staff_code || 'N/A'}</p>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Contact:</strong> {selectedCluster.tech_support_staff_contact || 'N/A'}</p>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Email:</strong> {selectedCluster.tech_support_staff_email || 'N/A'}</p>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>No Specialist Assigned</span>
                        )}
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card title="Coordinator" size="small" style={{ background: 'transparent', border: '1px solid var(--border-color)' }}>
                        {selectedCluster.coordinator_staff_name ? (
                          <div style={{ color: 'var(--text-color)' }}>
                            <h3 style={{ marginBottom: 8, color: 'var(--text-color)' }}>{selectedCluster.coordinator_staff_name}</h3>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Code:</strong> {selectedCluster.coordinator_staff_code || 'N/A'}</p>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Contact:</strong> {selectedCluster.coordinator_staff_contact || 'N/A'}</p>
                            <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Email:</strong> {selectedCluster.coordinator_staff_email || 'N/A'}</p>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>No Coordinator Assigned</span>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            },

            ...(userRole === 'ADMIN' ? [{
              key: 'access',

              label: `Cluster Access (${clusterAccessList.length})`,
              children: (
                <div style={{ padding: '12px 0' }}>
                  <div style={{ marginBottom: 16, borderBottom: '1px solid var(--border-color)', paddingBottom: 16 }}>
                    <Form form={accessForm} layout="inline" onFinish={handleGrantAccess}>
                      <Form.Item
                        name="login_ids"
                        label={<span style={{ color: 'var(--text-color)' }}>Grant Studio Access</span>}
                        rules={[{ required: true, message: 'Select at least one studio user' }]}
                        style={{ minWidth: 350 }}
                      >
                        <Select showSearch
                          mode="multiple"
                          placeholder="Select Studio Users..."
                          style={{ width: '100%' }}
                          allowClear
                          optionFilterProp="label"
                          options={availableLogins
                            .filter(l => !clusterAccessList.some(a => a.login_id === l.login_id))
                            .map(l => ({
                              value: l.login_id,
                              label: `${l.login_name || l.login_username} (${l.login_username})`
                            }))}
                        />
                      </Form.Item>
                      <Form.Item>
                        <Button type="primary" htmlType="submit" loading={grantLoading}>
                          Grant Access
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>

                  <Table
                    dataSource={clusterAccessList}
                    loading={accessLoading}
                    rowKey="cluster_access_id"
                    size="small"
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: 'Studio User',
                        dataIndex: 'login_name',
                        key: 'login_name',
                        render: (text, rec) => (
                          <div style={{ color: 'var(--text-color)' }}>
                            <strong>{text || rec.login_username}</strong>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>@{rec.login_username}</div>
                          </div>
                        )
                      },
                      {
                        title: 'Role',
                        dataIndex: 'login_role',
                        key: 'login_role',
                        render: (role) => <Tag color="blue">{role}</Tag>
                      },
                      {
                        title: 'Status',
                        dataIndex: 'access',
                        key: 'access',
                        render: (status) => <Tag color={status === 'Active' ? 'green' : 'red'}>{status}</Tag>
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, record) => (
                          <Popconfirm title="Revoke access for this user?" onConfirm={() => handleRevokeAccess(record.cluster_access_id)}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        )
                      }
                    ]}
                  />
                </div>
              )
            }] : [])
          ]} />
        )}
      </Modal>

      {/* Link/Edit Institution Modal */}
      <Modal 
        title={editingLink ? 'Edit Institution Affiliation' : 'Link Institution'} 
        open={isLinkOpen} 
        onCancel={() => setIsLinkOpen(false)} 
        footer={null} 
        destroyOnClose
      >
        <Form form={linkForm} layout="vertical" onFinish={handleSaveLink}>
          {!editingLink && (
            <Form.Item name="institution_id" label="Select Institution" rules={[{ required: true, message: 'Please select institution!' }]}>
              <Select showSearch optionFilterProp="children" placeholder="Choose Institution to link">
                {institutions.map(inst => (
                  <Select.Option key={inst.institution_id} value={inst.institution_id}>
                    {inst.institution_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="cluster_institution_code" label="Institution Code" rules={[{ required: true, message: 'Please input Institution code!' }]}>
            <Input placeholder="e.g. SC-101" />
          </Form.Item>
          <Form.Item name="cluster_institution_equipment_type" label="Equipment Type" rules={[{ required: true }]} initialValue="MANIPAL FOUNDATION">
            <Select showSearch optionFilterProp="children">
              <Select.Option value="MANIPAL FOUNDATION">Manipal Foundation</Select.Option>
              <Select.Option value="MAHAMAYA FOUNDATION">Mahamaya Foundation</Select.Option>
              <Select.Option value="Institution OWN EQUIPMENT">Institution Own Equipment</Select.Option>
            </Select>
          </Form.Item>
          {editingLink && (
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="children">
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}

          {/* Candidate Strength per Standard */}
          {(clusterStandards || []).filter(s => s && s.standard_id).length > 0 && (
            <>
              <Divider style={{ margin: '16px 0', borderColor: 'var(--border-color)' }}>Candidate Strength per Standard</Divider>
              {(clusterStandards || []).filter(s => s && s.standard_id).map(std => (
                <Form.Item
                  key={std.standard_id}
                  name={['standard_strengths', std.standard_id]}
                  label={`${std.standard_name || `Standard ${std.standard_id}`} Strength`}
                  initialValue={0}
                >
                  <InputNumber min={0} placeholder="Enter candidate strength" style={{ width: '100%' }} />
                </Form.Item>
              ))}
            </>
          )}

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsLinkOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Direct Candidate Strength Modal */}
      <Modal
        title={`Candidate Strength - ${selectedStrengthInst?.institution_name || 'Institution'}`}
        open={isStrengthModalOpen}
        onCancel={() => setIsStrengthModalOpen(false)}
        footer={null}
        destroyOnClose
        width={500}
      >
        <Form form={strengthForm} layout="vertical" onFinish={handleSaveStrengthsDirect}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            Configure candidate strength for each standard linked to this cluster:
          </p>
          {(clusterStandards || []).filter(s => s && s.standard_id).length > 0 ? (
            (clusterStandards || []).filter(s => s && s.standard_id).map(std => (
              <Form.Item
                key={std.standard_id}
                name={['standard_strengths', std.standard_id]}
                label={`${std.standard_name || `Standard ${std.standard_id}`} Candidate Strength`}
                initialValue={0}
              >
                <InputNumber min={0} placeholder="e.g. 45" style={{ width: '100%' }} />
              </Form.Item>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)' }}>
              No standards linked to this cluster yet. Please link standards in the Linked Standards tab first.
            </div>
          )}
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsStrengthModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={savingStrength}>
                Save Strength
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Linked Institution View Modal */}
      <Modal
        title={selectedInstRecord ? `${selectedInstRecord.institution_name} - Details` : 'Institution Information'}
        open={isInstDetailsOpen}
        onCancel={() => setIsInstDetailsOpen(false)}
        footer={null}
        width={650}
        destroyOnClose
      >
        {selectedInstRecord && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Institution Code">
              <Badge status="processing" text={selectedInstRecord.cluster_institution_code || 'N/A'} />
            </Descriptions.Item>
            <Descriptions.Item label="Institution Name">
              <strong>{selectedInstRecord.institution_name}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Head of Institution (Principal)">
              {selectedInstRecord.institution_head || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Location / Address">
              <Space direction="vertical" size={2}>
                <span>{selectedInstRecord.institution_address || 'N/A'}</span>
                {(selectedInstRecord.institution_map || selectedInstRecord.institution_address) && (
                  <a
                    href={selectedInstRecord.institution_map || `https://maps.google.com/?q=${encodeURIComponent(selectedInstRecord.institution_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12 }}
                  >
                    Open Location Map ↗
                  </a>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Contact Number">
              {selectedInstRecord.institution_contact || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Address">
              {selectedInstRecord.institution_email ? (
                <a href={`mailto:${selectedInstRecord.institution_email}`}>{selectedInstRecord.institution_email}</a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Equipment Type">
              <Tag color="purple">{selectedInstRecord.cluster_institution_equipment_type || 'N/A'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Medium">
              <Tag color={selectedInstRecord.institution_medium === 'english' ? 'blue' : 'warning'}>
                {(selectedInstRecord.institution_medium || 'N/A').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedInstRecord.status === 'Active' ? 'green' : 'red'}>
                {selectedInstRecord.status || 'Active'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Link Standard Modal */}
      <Modal 
        title={editingClusterStandard ? 'Edit Cluster Standard' : 'Link Standard to Cluster'} 
        open={isLinkStandardOpen} 
        onCancel={() => { setIsLinkStandardOpen(false); setEditingClusterStandard(null); }} 
        footer={null} 
        destroyOnClose
      >
        <Form form={standardForm} layout="vertical" onFinish={handleSaveClusterStandard} initialValues={{ instructor_medium: 'ENGLISH', status: 'Active' }}>
          {!editingClusterStandard && (
            <Form.Item 
              name="standard_id" 
              label="Select Standard" 
              rules={[{ required: true, message: 'Please select a standard' }]}
              extra={clusterAcademicYear ? `Showing standards for Academic Year: ${clusterAcademicYear.academic_year}` : undefined}
            >
              <Select 
                showSearch 
                optionFilterProp="children" 
                placeholder="Choose a standard"
                notFoundContent="No standards available to link for this academic year"
              >
                {allStandardsList
                  .filter(std => {
                    const matchesYear = !clusterYearId || !std.academic_year_id || std.academic_year_id === clusterYearId || std.academic_year_id === Number(clusterYearId);
                    const isAlreadyLinked = clusterStandards.some(cs => cs.standard_id === std.standard_id);
                    return matchesYear && !isAlreadyLinked;
                  })
                  .map(std => (
                    <Select.Option key={std.standard_id} value={std.standard_id}>
                      {std.standard_name} {std.standard_code ? `(${std.standard_code})` : ''} {std.academic_year ? `— [${std.academic_year}]` : ''}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="instructor_medium" label="Instructor Medium" rules={[{ required: true, message: 'Please select instructor medium' }]}>
            <Select showSearch optionFilterProp="children">
              <Select.Option value="KANNADA">KANNADA</Select.Option>
              <Select.Option value="ENGLISH">ENGLISH</Select.Option>
              <Select.Option value="KANNADA/ENGLISH">KANNADA/ENGLISH</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children">
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setIsLinkStandardOpen(false); setEditingClusterStandard(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* Copy Cluster & Linked Institutions Modal */}
      <Modal
        title={
          <Space>
            <CopyOutlined style={{ color: '#1890ff', fontSize: 18 }} />
            <span style={{ fontWeight: 600 }}>
              {copyModalCluster ? `Copy Cluster: ${copyModalCluster.cluster_name}` : 'Copy Cluster(s) to Academic Year'}
            </span>
          </Space>
        }
        open={isCopyModalOpen}
        onCancel={() => { setIsCopyModalOpen(false); setCopyModalCluster(null); copyForm.resetFields(); }}
        onOk={() => copyForm.submit()}
        confirmLoading={copyLoading}
        okText="Copy Cluster"
        width={620}
        destroyOnClose
      >
        <Form 
          form={copyForm} 
          layout="vertical" 
          onFinish={handleCopySubmit}
          initialValues={{ copy_institutions: true }}
        >
          <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(24, 144, 255, 0.08)', borderRadius: 8, border: '1px solid rgba(24, 144, 255, 0.2)' }}>
            <span style={{ fontSize: 13, color: '#1890ff', lineHeight: 1.5, display: 'block' }}>
              Clone cluster(s) and all linked institutions into the selected target academic year. (Note: standard mappings will NOT be copied).
            </span>
          </div>

          <Form.Item 
            name="source_cluster_ids" 
            label="Source Cluster(s) to Copy" 
            rules={[{ required: true, message: 'Please select at least one cluster to copy' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="Select Cluster(s)"
              style={{ width: '100%' }}
              optionFilterProp="children"
            >
              {data.map(c => {
                const ay = academicYears.find(a => a.academic_year_id === c.academic_year_id);
                return (
                  <Select.Option key={c.cluster_id} value={c.cluster_id}>
                    {c.cluster_name} ({c.cluster_code || 'No Code'}) {ay ? `— [${ay.academic_year}]` : ''}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item 
            name="target_academic_year_id" 
            label="Target Academic Year (Copy To)" 
            rules={[{ required: true, message: 'Please select target academic year' }]}
          >
            <Select placeholder="Select Target Academic Year">
              {academicYears.map(ay => (
                <Select.Option key={ay.academic_year_id} value={ay.academic_year_id}>
                  {ay.academic_year} {ay.status === 'active' ? '(Active)' : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8, marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>Include Components in Copy:</div>
            
            <Form.Item name="copy_institutions" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox defaultChecked>
                <span style={{ fontSize: 13 }}>
                  <strong>Include All Linked Institutions</strong> (Clones institution links, codes, and equipment types to the target cluster)
                </span>
              </Checkbox>
            </Form.Item>
          </Card>
        </Form>
      </Modal>
              </>
            )
          }
        ]}
      />
    </div>
  );
};

export default ClusterManagementPage;
